import { formatDateString } from "@cp/toolkit";
import {
  type BrokerLostOpportunitiesQuery,
  type BrokerOpportunitiesQuery,
  type BrokerPoliciesQuery,
  type BrokerQuotesQuery,
  type BrokerUpcomingRenewalsQuery,
  MarketingPlanState,
  QuoteState,
  SubmissionState,
  useBrokerLostOpportunitiesQuery,
  useBrokerOpportunitiesQuery,
  useBrokerPoliciesQuery,
  useBrokerQuotesQuery,
  useBrokerRenewalsQuery,
  useBrokerUpcomingRenewalsQuery,
} from "@qw/graphql";
import { type Dictionary, groupBy, uniqBy } from "lodash";
import { createContext, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import { marketingPlansToBookItems } from "@/book/marketing-plan-book-items";

type BrokerOpportunities = BrokerOpportunitiesQuery["brokerOpportunities"];
type BrokerLostOpportunities = BrokerLostOpportunitiesQuery["brokerLostOpportunities"];
type BrokerPolicies = BrokerPoliciesQuery["brokerPolicies"];
type BrokerQuotes = BrokerQuotesQuery["brokerQuotes"];
type BrokerUpcomingRenewal = BrokerUpcomingRenewalsQuery["brokerUpcomingRenewals"][number];

export interface BookContextValue {
  bindRequested: BrokerQuotes;
  bound: BrokerQuotes;
  brokerReview: BrokerOpportunities;
  delivered: BrokerQuotes;
  informationGathering: BrokerOpportunities;
  isBookOpen: boolean;
  isBookViewExpanded: boolean;
  lost: Dictionary<BrokerLostOpportunities>;
  onSelectInsured?: (insured: { id: string; name: string }) => void;
  pending: BrokerQuotes;
  quoted: BrokerOpportunities;
  quotedRenewals: BrokerOpportunities;
  upcomingRenewals: BrokerUpcomingRenewal[];
  submitted: BrokerOpportunities;
  submittedRenewals: BrokerOpportunities;
  showExpired: boolean;
  tab: string;
  thisYear: number;
  unsubmitted: BrokerOpportunities;
  unsolicitedUnsubmittedRenewals: BrokerOpportunities;
  solicitedUnsubmittedRenewals: BrokerOpportunities;
  won: Dictionary<BrokerPolicies>;
  years: number[];
  setIsBookOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsBookViewExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setShowExpired: React.Dispatch<React.SetStateAction<boolean>>;
  setTab: React.Dispatch<React.SetStateAction<string>>;
  setYears: React.Dispatch<React.SetStateAction<number[]>>;
}

export const BookContext = createContext<BookContextValue | null>(null);

const pendingSubmissions = new Set([SubmissionState.Pending, SubmissionState.ReadyToSubmit, SubmissionState.RuledOut]);
const marketingPlanBookStates = new Set<MarketingPlanState>([
  MarketingPlanState.InformationGathering,
  MarketingPlanState.InformationReview,
]);
const thisYear = new Date().getFullYear();

export const BookProvider = ({
  children,
  onSelectInsured,
}: React.PropsWithChildren<{ onSelectInsured?: (insured: { id: string; name: string }) => void }>) => {
  const [isBookOpen, setIsBookOpen] = useLocalStorage("qwibIsBookOpen", true);
  const [isBookViewExpanded, setIsBookViewExpanded] = useLocalStorage("qwibIsBookViewExpanded", true);
  const [tab, setTab] = useLocalStorage("brokerTab", "new");
  const [years, setYears] = useLocalStorage("beginYear", [thisYear, thisYear + 1]);
  const [showExpired, setShowExpired] = useLocalStorage("showExpired", false);

  const { data: { brokerOpportunities } = {} } = useBrokerOpportunitiesQuery({
    variables: {
      input: { showExpired },
    },
  });

  const { data: { brokerLostOpportunities } = {} } = useBrokerLostOpportunitiesQuery({
    variables: {
      input: { showActive: false },
    },
  });

  const { data: { brokerQuotes } = {} } = useBrokerQuotesQuery();

  const { data: { brokerUpcomingRenewals = [] } = {} } = useBrokerUpcomingRenewalsQuery();

  const { data: { brokerRenewals } = {} } = useBrokerRenewalsQuery({
    variables: {
      input: { showExpired },
    },
  });

  const { data: { brokerPolicies } = {} } = useBrokerPoliciesQuery({
    variables: {
      input: {
        beginYear: years[0],
        endYear: years[1],
      },
    },
  });

  const marketingOpportunities = useMemo(
    () => brokerOpportunities?.filter((opportunity) => !marketingPlanBookStates.has(opportunity.state)),
    [brokerOpportunities]
  );

  const { unsubmitted, submitted, quoted } = useMemo(
    () => categorizeOpportunities(marketingOpportunities),
    [marketingOpportunities]
  );

  const informationGathering = useMemo(
    () => marketingPlansToBookItems(brokerOpportunities, MarketingPlanState.InformationGathering),
    [brokerOpportunities]
  );

  const brokerReview = useMemo(
    () => marketingPlansToBookItems(brokerOpportunities, MarketingPlanState.InformationReview),
    [brokerOpportunities]
  );

  const { bindRequested, bound, delivered, pending } = useMemo(() => {
    if (!brokerQuotes) {
      return { bindRequested: [], bound: [], delivered: [], pending: [] };
    }

    const categorized = brokerQuotes.reduce<{
      bindRequested: BrokerQuotes;
      bound: BrokerQuotes;
      delivered: BrokerQuotes;
      pending: BrokerQuotes;
    }>(
      (acc, quote) => {
        switch (quote.state) {
          case QuoteState.BindRequestReceived:
            acc.bindRequested.push(quote);
            break;
          case QuoteState.Bound:
            acc.bound.push(quote);
            break;
          case QuoteState.Delivered:
            acc.delivered.push(quote);
            break;
          default:
            acc.pending.push(quote);
            break;
        }
        return acc;
      },
      { bindRequested: [], bound: [], delivered: [], pending: [] }
    );

    return {
      bindRequested: uniqBy(categorized.bindRequested, (quote) => quote.insured.id),
      bound: uniqBy(categorized.bound, (quote) => quote.insured.id),
      delivered: uniqBy(categorized.delivered, (quote) => quote.insured.id),
      pending: uniqBy(categorized.pending, (quote) => quote.insured.id),
    };
  }, [brokerQuotes]);

  // Memoized like every other list here: a fresh array each render invalidates the fuzzy-search
  // indexes keyed on it, which would rebuild on every keystroke of the insured search.
  const upcomingRenewals = useMemo(
    () => uniqBy(brokerUpcomingRenewals, (renewal) => renewal.insured.id),
    [brokerUpcomingRenewals]
  );

  const {
    unsubmitted: unsubmittedRenewals,
    submitted: submittedRenewals,
    quoted: quotedRenewals,
  } = useMemo(() => categorizeOpportunities(brokerRenewals), [brokerRenewals]);

  const { solicitedUnsubmittedRenewals, unsolicitedUnsubmittedRenewals } = useMemo(() => {
    const solicited: BrokerOpportunities = [];
    const unsolicited: BrokerOpportunities = [];

    unsubmittedRenewals.forEach((opp) => {
      if (opp.classificationLabels.includes("renewal-notice")) {
        solicited.push(opp);
      } else {
        unsolicited.push(opp);
      }
    });

    return {
      solicitedUnsubmittedRenewals: solicited,
      unsolicitedUnsubmittedRenewals: unsolicited,
    };
  }, [unsubmittedRenewals]);

  const won = useMemo(() => {
    if (!brokerPolicies) {
      return {};
    }

    return groupBy(
      uniqBy(brokerPolicies, ({ insured }) => insured.id),
      (policy) => formatDateString(policy.effectiveDate, "QQQ yyyy")
    );
  }, [brokerPolicies]);

  const lost = useMemo(() => {
    if (!brokerLostOpportunities) {
      return {};
    }

    return groupBy(
      uniqBy(brokerLostOpportunities, ({ insured }) => insured.id),
      (opportunity) => formatDateString(opportunity.desiredEffectiveDate, "QQQ yyyy")
    );
  }, [brokerLostOpportunities]);

  const value: BookContextValue = useMemo(
    () => ({
      bindRequested,
      bound,
      brokerReview,
      delivered,
      informationGathering,
      isBookOpen,
      isBookViewExpanded,
      lost,
      onSelectInsured,
      pending,
      quoted,
      quotedRenewals,
      upcomingRenewals,
      showExpired,
      submitted,
      submittedRenewals,
      tab,
      thisYear,
      unsubmitted,
      solicitedUnsubmittedRenewals,
      unsolicitedUnsubmittedRenewals,
      won,
      years,
      setIsBookOpen,
      setIsBookViewExpanded,
      setShowExpired,
      setTab,
      setYears,
    }),
    [
      bindRequested,
      bound,
      brokerReview,
      delivered,
      informationGathering,
      isBookOpen,
      isBookViewExpanded,
      lost,
      onSelectInsured,
      pending,
      quoted,
      quotedRenewals,
      upcomingRenewals,
      showExpired,
      submitted,
      submittedRenewals,
      tab,
      unsubmitted,
      solicitedUnsubmittedRenewals,
      unsolicitedUnsubmittedRenewals,
      years,
      won,
      setIsBookOpen,
      setIsBookViewExpanded,
      setShowExpired,
      setTab,
      setYears,
    ]
  );

  return <BookContext.Provider value={value}>{children}</BookContext.Provider>;
};

function categorizeOpportunities(opportunities: BrokerOpportunities | undefined) {
  if (!opportunities) {
    return { unsubmitted: [], submitted: [], quoted: [] };
  }

  const categorized = opportunities.reduce<{
    unsubmitted: BrokerOpportunities;
    submitted: BrokerOpportunities;
    quoted: BrokerOpportunities;
  }>(
    (acc, opp) => {
      const isUnsubmitted =
        opp.submissions?.length === 0 ||
        opp.submissions?.every((submission) => pendingSubmissions.has(submission.state));

      if (isUnsubmitted) {
        acc.unsubmitted.push(opp);
      } else {
        const hasQuotes = opp.submissions?.some((submission) => submission.quotes?.length > 0);
        if (hasQuotes) {
          acc.quoted.push(opp);
        } else {
          acc.submitted.push(opp);
        }
      }
      return acc;
    },
    { unsubmitted: [], submitted: [], quoted: [] }
  );

  return {
    unsubmitted: uniqBy(categorized.unsubmitted, (opp) => opp.insured.id),
    submitted: uniqBy(categorized.submitted, (opp) => opp.insured.id),
    quoted: uniqBy(categorized.quoted, (opp) => opp.insured.id),
  };
}
